import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { FileText, Plus, Search, Download, Eye, Trash2, Loader2, Pencil } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function PrescriptionList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const { data: prescriptions, isLoading } = useQuery({
    queryKey: ["prescriptions"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("prescriptions")
        .select(`
          *,
          patients_onboarding!inner(full_name, data_nascimento),
          prescription_items(count)
        `)
        .eq("professional_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (prescriptionId: string) => {
      const { error } = await supabase
        .from("prescriptions")
        .delete()
        .eq("id", prescriptionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prescriptions"] });
      toast.success("Prescrição excluída com sucesso!");
    },
    onError: () => {
      toast.error("Erro ao excluir prescrição");
    },
  });

  const filteredPrescriptions = prescriptions?.filter((p) => {
    const matchesSearch = p.patients_onboarding?.full_name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statusColors: Record<string, string> = {
    draft: "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300",
    completed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
    shared: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  };

  const statusLabels: Record<string, string> = {
    draft: "Rascunho",
    completed: "Concluída",
    shared: "Compartilhada",
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-500">
            <FileText className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Prescrição de Receitas</h1>
            <p className="text-muted-foreground">
              Gerencie prescrições médicas e nutricionais
            </p>
          </div>
        </div>

        <Button
          onClick={() => navigate("/app/aplicativo/prescription/novo")}
          className="w-full sm:w-auto bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nova Prescrição
        </Button>
      </div>

      <Card className="border-teal-200">
        <CardHeader>
          <CardTitle className="text-teal-700 dark:text-teal-300">
            Minhas Prescrições
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3 mb-4">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome do paciente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={statusFilter === null ? "default" : "outline"}
                onClick={() => setStatusFilter(null)}
                size="sm"
                className="flex-1 sm:flex-none"
              >
                Todos
              </Button>
              <Button
                variant={statusFilter === "draft" ? "default" : "outline"}
                onClick={() => setStatusFilter("draft")}
                size="sm"
                className="flex-1 sm:flex-none"
              >
                Rascunhos
              </Button>
              <Button
                variant={statusFilter === "completed" ? "default" : "outline"}
                onClick={() => setStatusFilter("completed")}
                size="sm"
                className="flex-1 sm:flex-none"
              >
                Concluídas
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
            </div>
          ) : filteredPrescriptions && filteredPrescriptions.length > 0 ? (
            <>
              {/* Mobile: Cards */}
              <div className="sm:hidden space-y-3">
                {filteredPrescriptions.map((prescription) => (
                  <Card key={prescription.id} className="p-4 border-teal-200">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-medium text-foreground">
                          {prescription.patients_onboarding?.full_name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {prescription.prescription_items?.[0]?.count || 0} item(ns)
                        </p>
                      </div>
                      <Badge className={statusColors[prescription.status || "draft"]}>
                        {statusLabels[prescription.status || "draft"]}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      {format(new Date(prescription.created_at), "dd/MM/yyyy", {
                        locale: ptBR,
                      })}
                    </p>
                    <div className="flex gap-2">
                      {prescription.status === "draft" && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() =>
                            navigate(`/app/aplicativo/prescription/novo?edit=${prescription.id}`)
                          }
                        >
                          <Pencil className="h-4 w-4 mr-1" /> Editar
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() =>
                          navigate(`/app/aplicativo/prescription/${prescription.id}`)
                        }
                      >
                        <Eye className="h-4 w-4 mr-1" /> Ver
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" size="sm" className="text-red-500">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Excluir prescrição?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Esta ação não pode ser desfeita.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteMutation.mutate(prescription.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Excluir
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Desktop: Table */}
              <div className="hidden sm:block rounded-lg border border-teal-200 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-teal-50 dark:bg-teal-950/20">
                      <TableHead>Paciente</TableHead>
                      <TableHead>Itens</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPrescriptions.map((prescription) => (
                      <TableRow key={prescription.id}>
                        <TableCell className="font-medium">
                          {prescription.patients_onboarding?.full_name}
                        </TableCell>
                        <TableCell>
                          {prescription.prescription_items?.[0]?.count || 0} item(ns)
                        </TableCell>
                        <TableCell>
                          {format(new Date(prescription.created_at), "dd/MM/yyyy", {
                            locale: ptBR,
                          })}
                        </TableCell>
                        <TableCell>
                          <Badge className={statusColors[prescription.status || "draft"]}>
                            {statusLabels[prescription.status || "draft"]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {prescription.status === "draft" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  navigate(`/app/aplicativo/prescription/novo?edit=${prescription.id}`)
                                }
                                title="Editar rascunho"
                              >
                                <Pencil className="h-4 w-4 text-blue-500" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                navigate(`/app/aplicativo/prescription/${prescription.id}`)
                              }
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Excluir prescrição?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Esta ação não pode ser desfeita.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => deleteMutation.mutate(prescription.id)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Excluir
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground mb-4">
                {searchTerm || statusFilter
                  ? "Nenhuma prescrição encontrada com esses filtros"
                  : "Nenhuma prescrição criada ainda"}
              </p>
              {!searchTerm && !statusFilter && (
                <Button
                  onClick={() => navigate("/app/aplicativo/prescription/novo")}
                  className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Primeira Prescrição
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
